import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Cron from 'react-cron-generator';
import {
  Col,
  DatePicker,
  Divider,
  InputNumber,
  Modal,
  Radio,
  Row,
  TimePicker
} from 'antd';
import moment from 'moment';

import {
  CronBuilderContainer,
  FrequencyBasedModeContainer,
  ModalHeaderContainer
} from './styled';
import {
  convertISOToDateTimeWithoutSecondsWithTZ,
  getCurrentTimezoneText,
  getTimeDifferenceFromUTC
} from '../../../util/timezone';
import { CRON_EXPRESSION_MODE, FREQUENCY_BASED_MODE } from './constants';
import { en } from '../Pipelines/constants';

const getDefaultData = () => ({
  days: 0,
  hours: 3,
  startDate: moment(),
  startTime: moment(),
  endTime: moment(),
  endDate: moment()
});

const getDefaultErrors = () => ({
  days: '',
  hours: '',
  startDate: '',
  startTime: '',
  endTime: '',
  endDate: ''
});

const SchedulePipelineModal = ({
  open,
  onClose,
  apiActions,
  selectedPipeline,
  pipelinesScheduleInfo,
  pipelinesScheduleInfoLoading,
  moduleId
}) => {
  const [cronExp, setCronExp] = useState('');

  const scheduled = useMemo(
    () =>
      !!pipelinesScheduleInfo?.pipelineId && pipelinesScheduleInfo?.id !== 0,
    [pipelinesScheduleInfo]
  );

  const [mode, setMode] = useState(CRON_EXPRESSION_MODE);
  const [data, setData] = useState(getDefaultData());
  const [errors, setErrors] = useState(getDefaultErrors());

  useEffect(() => {
    if (mode === CRON_EXPRESSION_MODE) {
      return () => {
        setCronExp('');
      };
    }

    return () => {};
  }, [mode]);

  useEffect(() => {
    if (open) {
      return () => {
        setCronExp('');
        setMode(CRON_EXPRESSION_MODE);
        setData(getDefaultData());
        setErrors(getDefaultErrors());
      };
    }

    return () => {};
  }, [open]);

  const handleModeChange = (event) => {
    const { value } = event.target;
    setMode(value);
  };

  const startValidation = (updatedData) => {
    const formData = updatedData ? updatedData : data;
    const errorsData = getDefaultErrors();

    if (mode == FREQUENCY_BASED_MODE) {
      // Convert both start time and end time to timestamps
      const startDateTimestamp = formData.startDate
        ? formData.startDate.valueOf()
        : 0;
      const endDateTimestamp = formData.endDate
        ? formData.endDate.valueOf()
        : 0;

      const startTimestamp = formData.startTime
        ? formData.startTime.valueOf()
        : 0;
      const endTimestamp = formData.endTime ? formData.endTime.valueOf() : 0;

      // Check if end time is less than start time
      if (
        endDateTimestamp <= startDateTimestamp &&
        endTimestamp <= startTimestamp
      ) {
        // Display an error message or take appropriate action
        errorsData.endDate = 'End time cannot be less or equal to start time';
      }

      if (formData.startTime.isBefore()) {
        errorsData.startDate = 'Start time should be in future.';
      }
    }

    setErrors(errorsData);

    return Object.values(errorsData).some((e) => !!e);
  };

  const isValid = useMemo(
    () => !Object.values(errors).some((e) => !!e),
    [errors]
  );

  const handleFrequencyInputChange = (name, value) => {
    if ((name === 'days' || name === 'hours') && value < 0) {
      return;
    }

    if (name === 'hours' && value < 1) {
      return;
    }

    const newData = {
      ...data,
      [name]: value
    };

    if (name === 'startDate') {
      newData['startTime'] = value;
    }

    if (name === 'endDate') {
      newData['endTime'] = value;
    }

    // If validation passes, update the data state
    setData(() => newData);
    startValidation(newData);
  };

  const schedulePipeline = () => {
    if (!scheduled && startValidation()) {
      return;
    }
    const finalStartTime =
      data?.startDate.valueOf() > data?.startTime.valueOf()
        ? data?.startDate
        : data?.startTime;
    const finalEndTime =
      data?.endDate.valueOf() > data?.endTime.valueOf()
        ? data?.endDate
        : data?.endTime;
    let payload = { id: selectedPipeline?.id, moduleId };
    if (mode == CRON_EXPRESSION_MODE) {
      payload.data = { cronExpression: cronExp };
    } else {
      payload.data = {
        startTime: moment(finalStartTime).unix() * 1000,
        endTime: moment(finalEndTime).unix() * 1000,
        frequency:
          (data?.seconds || 0) +
          (data?.minutes || 0) * 60 +
          (data?.hours || 0) * 60 * 60 +
          (data?.days || 0) * 60 * 60 * 24
      };
    }
    if (!scheduled) {
      apiActions.postAMLSCommonPipelinesSchedule(payload);
    } else {
      apiActions.postAMLSCommonPipelinesUnSchedule({
        id: selectedPipeline?.id,
        moduleId: moduleId
      });
    }
  };

  const toHHMMSS = (totalSeconds) => {
    let seconds = Math.floor(totalSeconds),
      hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    let minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    const pad = (n) => (n < 10 ? '0' + n : n);
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const format = (seconds) => {
    return toHHMMSS(seconds);
  };

  const disabledDate = (current) => {
    // Disable dates before today (including today)
    return current && current < moment().startOf('day');
  };

  return (
    <Modal
      title={
        <ModalHeaderContainer>
          <p className="title-text">{en.actions.schedule.modalTitle}</p>
          <p className="note-text">
            {!pipelinesScheduleInfoLoading && !scheduled // showing the note only if it is not scheduled
              ? en.actions.schedule.headerNote
                  .replace('{{hrs}}', getTimeDifferenceFromUTC())
                  .replace('{{tz}}', getCurrentTimezoneText())
              : null}
          </p>
        </ModalHeaderContainer>
      }
      visible={open}
      onOk={schedulePipeline}
      onCancel={onClose}
      width={700}
      okText={
        scheduled
          ? en.actions.schedule.unscheduleOkText
          : en.actions.schedule.scheduleOkText
      }
      destroyOnClose
      okButtonProps={{
        disabled:
          !isValid || (mode === CRON_EXPRESSION_MODE && !scheduled && !cronExp)
      }}
      className="schedule-pipeline-modal">
      {!pipelinesScheduleInfoLoading ? (
        <CronBuilderContainer>
          {scheduled ? (
            <div>
              <p className="header-text">
                {en.actions.schedule.currentSchedule}
              </p>
              <Divider />
              <div>
                {/* {selectedPipeline.schedule ? (
                <div>
                  <p>{selectedPipeline.schedule}</p>
                </div>
              ) : null} */}
                {pipelinesScheduleInfo?.startTime ? (
                  <div className="flex">
                    <div className="title-text">Start Time : </div>
                    <div>
                      &nbsp;&nbsp;
                      {convertISOToDateTimeWithoutSecondsWithTZ(
                        pipelinesScheduleInfo?.startTime
                      )}
                    </div>
                  </div>
                ) : null}
                {pipelinesScheduleInfo?.endTime ? (
                  <div className="flex">
                    <div className="title-text">End Time : </div>{' '}
                    <div>
                      &nbsp;&nbsp;
                      {convertISOToDateTimeWithoutSecondsWithTZ(
                        pipelinesScheduleInfo?.endTime
                      )}
                    </div>
                  </div>
                ) : null}
                {pipelinesScheduleInfo?.frequencySeconds ? (
                  <div className="flex">
                    <div className="title-text">Frequency : </div>{' '}
                    <div>
                      &nbsp;&nbsp;
                      {format(pipelinesScheduleInfo.frequencySeconds)} HRS
                    </div>
                  </div>
                ) : null}
                {pipelinesScheduleInfo?.cronExpression ? (
                  <div className="flex">
                    <div className="title-text">Cron Expression : </div>{' '}
                    <div>
                      &nbsp;&nbsp;
                      {pipelinesScheduleInfo.cronExpression}
                    </div>
                  </div>
                ) : null}
                {pipelinesScheduleInfo?.cronDescription ? (
                  <div className="flex">
                    <div className="title-text">Description : </div>{' '}
                    <div>
                      &nbsp;&nbsp;
                      {pipelinesScheduleInfo.cronDescription} {`( in UTC )`}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <Row gutter={24} className="mode-radio-wrapper">
                <Col span={6} className="title-text">
                  Mode of scheduler
                </Col>
                <Col span={18}>
                  <Radio.Group onChange={handleModeChange} value={mode}>
                    <Radio value={CRON_EXPRESSION_MODE} className="title-text">
                      {en.actions.schedule.cronExpressionMode}
                    </Radio>
                    <Radio value={FREQUENCY_BASED_MODE} className="title-text">
                      {en.actions.schedule.frequencyBasedMode}
                    </Radio>
                  </Radio.Group>
                </Col>
              </Row>
              <div>
                {mode === CRON_EXPRESSION_MODE ? (
                  <Cron
                    onChange={setCronExp}
                    value={cronExp}
                    showResultText
                    // showResultCron
                  />
                ) : (
                  <FrequencyBasedModeContainer>
                    <Row gutter={24}>
                      <Col span={6} className="title-text">
                        Start Time
                      </Col>
                      <Col span={9}>
                        <DatePicker
                          className="date-picker"
                          name="startDate"
                          value={data.startDate}
                          onChange={(value) =>
                            handleFrequencyInputChange('startDate', value)
                          }
                          disabledDate={disabledDate}
                        />
                        <span className="error-text">{errors.startDate}</span>
                      </Col>
                      <Col span={9}>
                        <TimePicker
                          className="time-picker"
                          name="startTime"
                          value={data.startTime}
                          onChange={(value) =>
                            handleFrequencyInputChange('startTime', value)
                          }
                        />
                        <span className="error-text">{errors.startTime}</span>
                      </Col>
                      <Divider />
                      <Col span={6} className="title-text">
                        End Time
                      </Col>
                      <Col span={9}>
                        <DatePicker
                          className="date-picker"
                          name="endDate"
                          value={data.endDate}
                          disabledDate={disabledDate}
                          onChange={(value) =>
                            handleFrequencyInputChange('endDate', value)
                          }
                        />
                        <span className="error-text">{errors.endDate}</span>
                      </Col>
                      <Col span={9}>
                        <TimePicker
                          className="time-picker"
                          name="endTime"
                          value={data.endTime}
                          onChange={(value) =>
                            handleFrequencyInputChange('endTime', value)
                          }
                        />
                        <span className="error-text">{errors.endTime}</span>
                      </Col>
                      <Divider />
                      <Col span={6} className="title-text">
                        Frequency
                      </Col>
                      <Col span={9} className="frequency-inputs-wrapper">
                        <div>
                          <p>Days</p>
                          <InputNumber
                            name="days"
                            value={data.days}
                            onChange={(value) =>
                              handleFrequencyInputChange('days', value)
                            }
                          />
                        </div>
                        <div>
                          <p>Hours</p>
                          <InputNumber
                            name="hours"
                            value={data.hours}
                            onChange={(value) =>
                              handleFrequencyInputChange('hours', value)
                            }
                          />
                        </div>
                      </Col>
                      <Col span={9}>Time period between runs</Col>
                    </Row>
                  </FrequencyBasedModeContainer>
                )}
              </div>
            </>
          )}
        </CronBuilderContainer>
      ) : null}
    </Modal>
  );
};

SchedulePipelineModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  apiActions: PropTypes.object.isRequired,
  selectedPipeline: PropTypes.object,
  pipelinesScheduleInfo: PropTypes.object,
  pipelinesScheduleInfoLoading: PropTypes.bool.isRequired,
  moduleId: PropTypes.string.isRequired
};

export default SchedulePipelineModal;
